import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, MailIcon } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { referenceGuides } from '../data/referenceGuides';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

/**
 * Contact / packet request.
 *
 * The reference packets are gated: the guide pages point their download button
 * here with ?packet=<slug>, we capture who is asking, and the download link is
 * emailed back. There is no backend in this app, so submit resolves locally and
 * the confirmation states plainly that the link arrives by email.
 */

const ALL_PACKETS = 'all';
interface Errors {
  name?: string;
  email?: string;
  company?: string;
}
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const FIELD_CLASS = 'w-full min-h-[44px] rounded-xl border border-white/[0.12] bg-white/[0.03] px-4 py-2.5 text-white text-sm placeholder:text-white/30 focus:border-violet-link/60 focus:outline-none transition-colors';
function FieldError({
  id,
  message
}: {
  id: string;
  message?: string;
}) {
  if (!message) return null;
  return <p id={id} className="text-callout-flag text-xs mt-1.5" data-id="element-583" data-mp="hzgx56ois2mq">
      {message}
    </p>;
}
export function ContactPage() {
  const {
    search
  } = useLocation();
  const posthog = usePostHog();
  const requestedPacket = useMemo(() => {
    const slug = new URLSearchParams(search).get('packet');
    return referenceGuides.some(g => g.slug === slug) ? slug as string : ALL_PACKETS;
  }, [search]);
  const sourceGuide = referenceGuides.find(g => g.slug === requestedPacket);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [packet, setPacket] = useState(requestedPacket);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    setPacket(requestedPacket);
  }, [requestedPacket]);
  useDocumentMeta({
    title: 'Get the reference packets | Thruline',
    description: 'Tell us where to send it and we will email you the download link for the Thruline reference packets on inventory, manufacturing execution, and procurement systems.'
  });
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Errors = {};
    if (!name.trim()) nextErrors.name = 'Please add your name.';
    if (!email.trim()) {
      nextErrors.email = 'We need an email to send the link to.';
    } else if (!EMAIL_PATTERN.test(email.trim())) {
      nextErrors.email = 'That email doesn’t look right.';
    }
    if (!company.trim()) nextErrors.company = 'Please add your company.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStatus('sending');
    posthog?.capture('reference_packet_requested', {
      packet,
      source_guide: sourceGuide?.slug ?? null
    });
    window.setTimeout(() => setStatus('sent'), 600);
  };
  const packetLabel = packet === ALL_PACKETS ? 'all three reference packets' : `the ${referenceGuides.find(g => g.slug === packet)?.title} packet`;
  return <div className="min-h-screen bg-navy text-white pt-32 pb-24 px-6" data-id="element-584" data-mp="26mllr9tcl3mp">
      <div className="max-w-5xl mx-auto" data-id="element-585" data-mp="1ixah3h8vmp3a">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.45
      }} className="max-w-2xl mb-12" data-id="element-586" data-mp="1iy2feztuz0x9">
          <span className="text-violet-link text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-587" data-mp="1ss2hestufq1r">
            Reference packets
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] mb-5" data-id="element-588" data-mp="348iw2jxkq2nk">
            {sourceGuide ? `Get the ${sourceGuide.title} packet` : 'Get the reference packets'}
          </h1>
          <p className="text-lg text-white/60 leading-relaxed" data-id="element-589" data-mp="18uygrazdhblx">
            Tell us where to send it and we’ll email you the download link — the full
            vendor-neutral packet, formatted to share with your team. No sales sequence,
            no gate on the guides themselves.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10 items-start" data-id="element-590" data-mp="1mr25wpve2trj">
          {status === 'sent' ? <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35
        }} className="rounded-2xl border border-guide-procurement/25 bg-guide-procurement/[0.06] p-8 min-w-0" role="status" data-id="element-591" data-mp="1rqf0xxwez757">
              <div className="w-11 h-11 rounded-full bg-guide-procurement/15 flex items-center justify-center mb-5" data-id="element-592" data-mp="2air58q6moihz">
                <CheckIcon aria-hidden="true" className="w-5 h-5 text-guide-procurement" data-id="element-593" data-mp="2fhasctwyk0bx" />
              </div>
              <h2 className="text-2xl font-bold mb-3" data-id="element-594" data-mp="2pm0vv480fci9">Request received</h2>
              <p className="text-white/70 leading-relaxed mb-6" data-id="element-595" data-mp="2kmxq1hk7h298">
                We’ll email the download link for {packetLabel} to{' '}
                <span className="text-white font-medium" data-id="element-596" data-mp="2focp1fx4qejq">{email.trim()}</span> shortly. If it
                hasn’t landed within a business day, reply to us at{' '}
                <a href="mailto:contact@thruline.co" className="text-violet-link hover:underline" data-id="element-597" data-mp="38bz3jj7wb3ld">
                  contact@thruline.co
                </a>
                .
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-598" data-mp="2ym76micbpydq">
                {sourceGuide ? <Link to={`/resources/reference-guides/${sourceGuide.slug}`} className="inline-flex items-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-colors" data-id="element-599" data-mp="2yk2s76gstgz7">
                    Keep reading the guide
                    <ArrowRightIcon aria-hidden="true" className="w-4 h-4" data-id="element-600" data-mp="3maqgh2b0uj7x" />
                  </Link> : <Link to="/resources/reference-guides" className="inline-flex items-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-colors" data-id="element-601" data-mp="1kezoiesmg8kk">
                    Browse the guides
                    <ArrowRightIcon aria-hidden="true" className="w-4 h-4" data-id="element-602" data-mp="1fnhopu2c4e16" />
                  </Link>}
                <button type="button" onClick={() => setStatus('idle')} className="text-white/55 text-sm hover:text-white transition-colors" data-id="element-603" data-mp="11o9iwibo27o0">
                  Use a different email
                </button>
              </div>
            </motion.div> : <form onSubmit={handleSubmit} noValidate className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-7 sm:p-8 min-w-0" data-id="element-604" data-mp="rr3s7b6ysiob">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-605" data-mp="wqq4nieyxuw2">
                <div data-id="element-606" data-mp="2m4mn0d9m1n5x">
                  <label htmlFor="contact-name" className="block text-sm font-medium text-white/70 mb-2" data-id="element-607" data-mp="2m20hska6zjqv">
                    Full name
                  </label>
                  <input id="contact-name" name="name" type="text" autoComplete="name" value={name} onChange={e => setName(e.target.value)} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'contact-name-error' : undefined} className={FIELD_CLASS} placeholder="Jordan Reyes" data-id="element-608" data-mp="fgckl1ntkxhz" />
                  <FieldError id="contact-name-error" message={errors.name} data-id="element-609" data-mp="kdmf2vq2fzco" />
                </div>

                <div data-id="element-610" data-mp="y512rfux4oq2">
                  <label htmlFor="contact-email" className="block text-sm font-medium text-white/70 mb-2" data-id="element-611" data-mp="y494fx9xscw3">
                    Work email
                  </label>
                  <input id="contact-email" name="email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'contact-email-error' : undefined} className={FIELD_CLASS} placeholder="jordan@company.com" data-id="element-612" data-mp="184i5ppiscoic" />
                  <FieldError id="contact-email-error" message={errors.email} data-id="element-613" data-mp="34xjyfx6zk9t0" />
                </div>
              </div>

              <div className="mt-5" data-id="element-614" data-mp="2j2tf7kv3acpw">
                <label htmlFor="contact-company" className="block text-sm font-medium text-white/70 mb-2" data-id="element-615" data-mp="2o2fstvetxuz8">
                  Company
                </label>
                <input id="contact-company" name="company" type="text" autoComplete="organization" value={company} onChange={e => setCompany(e.target.value)} aria-invalid={!!errors.company} aria-describedby={errors.company ? 'contact-company-error' : undefined} className={FIELD_CLASS} placeholder="Acme Manufacturing" data-id="element-616" data-mp="2suh3xe09hfno" />
                <FieldError id="contact-company-error" message={errors.company} data-id="element-617" data-mp="255o0yxl827jk" />
              </div>

              <div className="mt-5" data-id="element-618" data-mp="2a4r9is0euvwa">
                <label htmlFor="contact-packet" className="block text-sm font-medium text-white/70 mb-2" data-id="element-619" data-mp="2f2alfl9mywl2">
                  Which packet do you want?
                </label>
                <select id="contact-packet" name="packet" value={packet} onChange={e => setPacket(e.target.value)} className={FIELD_CLASS} data-id="element-620" data-mp="3lk66vwwj7prx">
                  <option value={ALL_PACKETS} data-id="element-621" data-mp="1esjaivg214kp">All three packets</option>
                  {referenceGuides.map(guide => <option key={guide.slug} value={guide.slug} data-id="element-622" data-mp="19vj3oibdrxsj">
                      {guide.title}
                    </option>)}
                </select>
              </div>

              <div className="mt-5" data-id="element-623" data-mp="104ybpdq7pril">
                <label htmlFor="contact-notes" className="block text-sm font-medium text-white/70 mb-2" data-id="element-624" data-mp="103vj91d8rh0k">
                  What are you trying to figure out?{' '}
                  <span className="text-white/35 font-normal" data-id="element-625" data-mp="r3gwdxkmptuy">(optional)</span>
                </label>
                <textarea id="contact-notes" name="notes" rows={4} value={notes} onChange={e => setNotes(e.target.value)} className={`${FIELD_CLASS} resize-y`} placeholder="Running production out of spreadsheets and an accounting system — trying to work out what we actually need." data-id="element-626" data-mp="m4dq69swxhle" />
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-5" data-id="element-627" data-mp="2bjk67sqhwg1d">
                <button type="submit" disabled={status === 'sending'} className="inline-flex items-center gap-2 min-h-[44px] px-7 py-3 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors" data-id="element-628" data-mp="xfevyycfbv0i">
                  <MailIcon aria-hidden="true" className="w-4 h-4" data-id="element-629" data-mp="16dz8tivazagt" />
                  {status === 'sending' ? 'Sending…' : 'Email me the download link'}
                </button>
                <p className="text-white/40 text-xs leading-relaxed max-w-[260px]" data-id="element-630" data-mp="epozm6t0j16h">
                  We use this to send the packet and nothing else. No list, no sequence.
                </p>
              </div>
            </form>}

          <aside className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6" data-id="element-631" data-mp="nj23g3sm7n7c">
            <h2 className="text-white/50 text-xs font-bold uppercase tracking-widest mb-4" data-id="element-632" data-mp="ioeclya52gpl">
              What’s in a packet
            </h2>
            <ul className="space-y-3 list-none mb-6" data-id="element-633" data-mp="3n58t96xlilm2">
              {['The full guide as a shareable PDF', 'Side-by-side system comparison tables', 'The diagnostic questions, in checklist form'].map(item => <li key={item} className="flex items-start gap-2.5 text-white/65 text-sm leading-relaxed" data-id="element-634" data-mp="3hr8tk5fbn48b">
                  <CheckIcon aria-hidden="true" className="w-4 h-4 text-violet-link flex-shrink-0 mt-0.5" data-id="element-635" data-mp="3334edbfh9aua" />
                  {item}
                </li>)}
            </ul>

            <div className="pt-5 border-t border-white/[0.08]" data-id="element-636" data-mp="37zcuos5eva31">
              <p className="text-white/50 text-sm leading-relaxed mb-3" data-id="element-637" data-mp="3vrt8gqi48fwj">
                Would rather just talk it through?
              </p>
              <a href="https://calendly.com/alex-thruline/learn-about-thruline" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-violet-link text-sm font-medium hover:gap-3 transition-all" data-id="element-638" data-mp="3lt3z5m4ac3rw">
                Book a time with us
                <ArrowRightIcon aria-hidden="true" className="w-4 h-4" data-id="element-639" data-mp="2acm51so5ud8f" />
              </a>
            </div>
          </aside>
        </div>
      </div>
    </div>;
}